import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Fade,
  Avatar,
  Stack,
  Link as MuiLink,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const Contact = () => {
  const theme = useTheme();
  const { actualMode } = useCustomTheme();
  const isDark = actualMode === 'dark';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: ''
  });

  const [touched, setTouched] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBlur = (e) => {
    setTouched({
      ...touched,
      [e.target.name]: true
    });
  };

  const validateField = (name, value) => {
    switch(name) {
      case 'name':
        return value.length < 2 ? 'Name must be at least 2 characters' : '';
      case 'email':
        return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Invalid email address' : '';
      case 'phone':
        return value && !/^[0-9+\-\s()]{10,15}$/.test(value) ? 'Invalid phone number' : '';
      case 'subject':
        return value.length < 3 ? 'Subject must be at least 3 characters' : '';
      case 'message':
        return value.length < 10 ? 'Message must be at least 10 characters' : '';
      default:
        return '';
    }
  };

  const getFieldError = (name) => {
    return touched[name] ? validateField(name, formData[name]) : '';
  };

  const isFormValid = () => {
    return formData.name.length >= 2 &&
           /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) &&
           formData.subject.length >= 3 &&
           formData.message.length >= 10;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!isFormValid()) {
      return;
    }

    setStatus({ loading: true, success: false, error: '' });

    try {
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        timestamp: serverTimestamp(),
        status: 'unread',
        userAgent: navigator.userAgent,
        pageUrl: window.location.href
      });

      setStatus({ loading: false, success: true, error: '' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTouched({});
      
      setTimeout(() => {
        setStatus(prev => ({ ...prev, success: false }));
      }, 5000);
    } catch (error) {
      console.error('Error sending message:', error);
      setStatus({ 
        loading: false, 
        success: false, 
        error: 'Failed to send message. Please try again later.' 
      });
    }
  };

  const features = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Industry Insights',
      description: 'Latest trends and analysis from tech experts'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'Security Best Practices',
      description: 'Keep your digital assets protected'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Performance Optimization',
      description: 'Speed up your applications effectively'
    }
  ];

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: 'Email Us',
      details: [
        { label: 'info@aediax.com', href: 'mailto:info@aediax.com', primary: true },
        { label: 'support@aediax.com', href: 'mailto:support@aediax.com', primary: false }
      ]
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 28 }} />,
      title: 'Call Us',
      details: [
        { label: '+91-7854998757', href: 'tel:+917854998757', primary: true }
      ]
    }
  ];

  const addresses = [
    {
      icon: <ApartmentIcon />,
      title: 'Corporate Address',
      details: 'Ghatikia, Shankarpur, Bhubaneswar - 751003',
      isPrimary: true
    },
    {
      icon: <HomeIcon />,
      title: 'Registered Office',
      details: 'Plot No. 117, Belpal Badsah, Suhagpura, Mayurbhanj, Barsahi, Orissa, India, 757026'
    }
  ];

  const directors = [
    {
      name: 'Amresh Bhuyan',
      role: 'CEO, Founder & Director',
      icon: <VerifiedIcon />,
      bio: 'Leading innovation and strategic growth with over a decade of experience'
    },
    {
      name: 'Sanat Kumar Bhuyan',
      role: 'Director',
      icon: <VerifiedIcon />,
      bio: 'Overseeing operations and governance with visionary leadership'
    }
  ];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, sm: 8, md: 12 },
          bgcolor: isDark ? theme.palette.grey[900] : theme.palette.grey[900],
          background: isDark 
            ? `linear-gradient(135deg, ${theme.palette.grey[900]} 0%, ${theme.palette.grey[800]} 100%)`
            : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 6 }, position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center', maxWidth: 900, mx: 'auto' }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2rem', sm: '3rem', md: '4.5rem' },
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.common.white} 0%, ${theme.palette.primary.light} 40%, ${theme.palette.secondary.light} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2,
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2
                }}
              >
                AediaX Blog
              </Typography>
              
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.25rem' },
                  color: alpha(theme.palette.common.white, 0.95),
                  mb: 3,
                  fontWeight: 600,
                  letterSpacing: '-0.01em'
                }}
              >
                Where Technology Meets Innovation
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: alpha(theme.palette.common.white, 0.75),
                  maxWidth: 700,
                  mx: 'auto',
                  mb: 4,
                  fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
                  lineHeight: 1.6
                }}
              >
                Discover expert insights, cutting-edge trends, and in-depth analysis from industry leaders. 
                Join our community of tech enthusiasts and stay ahead in the digital revolution.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ bgcolor: theme.palette.background.default }}>
        {/* Features Section */}
        <Box sx={{ py: { xs: 6, sm: 8, md: 12 }, bgcolor: theme.palette.background.paper }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
            <Fade in timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 6 }}>
                <Typography
                  variant="overline"
                  sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: 2, fontSize: '0.75rem' }}
                >
                  What We Offer
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    color: theme.palette.text.primary,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}
                >
                  Empowering Your Digital Journey
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ maxWidth: 700, mx: 'auto', color: theme.palette.text.secondary, fontSize: '1rem' }}
                >
                  AediaX Blog delivers premium content to help you navigate the complex world of technology
                </Typography>
              </Box>
            </Fade>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              justifyContent="center"
              alignItems="stretch"
            >
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.15 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      textAlign: 'center',
                      p: { xs: 2, sm: 3 },
                      transition: 'all 0.3s ease',
                      bgcolor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h5" fontWeight={700} sx={{ mb: 1.5, color: theme.palette.text.primary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              ))}
            </Stack>
          </Container>
        </Box>

        {/* Company Overview */}
        <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 12 }, px: { xs: 2, sm: 3, md: 6 } }}>
          <Fade in timeout={800}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="overline"
                sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: 2 }}
              >
                About AediaX Tech
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                }}
              >
                Private Limited
              </Typography>
              <Typography
                variant="body1"
                sx={{ maxWidth: 850, mx: 'auto', color: theme.palette.text.secondary, fontSize: { xs: '0.95rem', sm: '1rem' }, lineHeight: 1.7 }}
              >
                AediaX Tech Private Limited stands at the forefront of technological innovation, 
                delivering cutting-edge solutions that transform businesses and drive digital excellence. 
                With a commitment to quality and a passion for innovation, we help organizations thrive 
                in the ever-evolving digital landscape.
              </Typography>
            </Box>
          </Fade>

          {/* Directors Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 4,
                textAlign: 'center',
                color: theme.palette.text.primary,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
              }}
            >
              Our Leadership
            </Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              justifyContent="center"
              alignItems="stretch"
            >
              {directors.map((director, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.2 }}
                  style={{ flex: 1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
                      <Avatar
                        sx={{
                          width: { xs: 80, sm: 100 },
                          height: { xs: 80, sm: 100 },
                          mx: 'auto',
                          mb: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main
                        }}
                      >
                        {director.icon}
                      </Avatar>
                      <Typography variant="h5" fontWeight={800} sx={{ mb: 1, color: theme.palette.text.primary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        {director.name}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, mb: 2, fontWeight: 700 }}>
                        {director.role}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {director.bio}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Stack>
          </Box>

          {/* Contact Section */}
          <Box id="contact-form">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                mb: 4,
                textAlign: 'center',
                color: theme.palette.text.primary,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
              }}
            >
              Get in Touch With Us
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4 }}>
              {/* Contact Information */}
              <Box sx={{ flex: 1 }}>
                <motion.div
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                >
                  <Typography variant="body1" sx={{ mb: 4, color: theme.palette.text.secondary, fontSize: '0.95rem', lineHeight: 1.7 }}>
                    Have questions, feedback, or want to collaborate? We'd love to hear from you. 
                    Our team is ready to assist you with any inquiries.
                  </Typography>

                  {/* Addresses */}
                  <Box sx={{ mb: 4 }}>
                    {addresses.map((addr, idx) => (
                      <Card
                        key={idx}
                        elevation={0}
                        sx={{
                          mb: 2.5,
                          p: { xs: 2, sm: 3 },
                          borderRadius: 2,
                          bgcolor: addr.isPrimary ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[2],
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Box sx={{ color: theme.palette.primary.main, mt: 0.5 }}>{addr.icon}</Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5, color: theme.palette.text.primary }}>
                              {addr.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                              {addr.details}
                            </Typography>
                          </Box>
                        </Stack>
                      </Card>
                    ))}
                  </Box>

                  {/* Contact Details */}
                  <Stack spacing={2.5} sx={{ mb: 4 }}>
                    {contactInfo.map((item, idx) => (
                      <Card
                        key={idx}
                        elevation={0}
                        sx={{
                          p: { xs: 2, sm: 3 },
                          borderRadius: 2,
                          bgcolor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: theme.shadows[2],
                            borderColor: theme.palette.primary.main
                          }
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                          <Box sx={{ color: theme.palette.primary.main, mt: 0.5 }}>{item.icon}</Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1, color: theme.palette.text.primary }}>
                              {item.title}
                            </Typography>
                            {item.details.map((detail, didx) => (
                              detail.href ? (
                                <MuiLink
                                  key={didx}
                                  href={detail.href}
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    textDecoration: 'none',
                                    display: 'block',
                                    mb: detail.primary ? 0.5 : 0,
                                    fontSize: '0.875rem',
                                    '&:hover': { color: theme.palette.primary.main }
                                  }}
                                >
                                  {detail.label}
                                </MuiLink>
                              ) : (
                                <Typography key={didx} variant="body2" color="text.secondary">
                                  {detail.label}
                                </Typography>
                              )
                            ))}
                          </Box>
                        </Stack>
                      </Card>
                    ))}
                  </Stack>

                  {/* Business Hours */}
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 3 },
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" fontWeight={800} sx={{ color: theme.palette.text.primary, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Business Hours
                      </Typography>
                    </Stack>
                    <Box sx={{ ml: { xs: 0, sm: 5 } }}>
                      <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                        Monday - Friday: 9:00 AM - 6:00 PM
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                        Saturday: 10:00 AM - 4:00 PM
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.7) }}>
                        Sunday: Closed
                      </Typography>
                    </Box>
                  </Card>
                </motion.div>
              </Box>

              {/* Contact Form */}
              <Box sx={{ flex: 1 }}>
                <motion.div
                  initial={{ x: 30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: theme.shadows[4]
                      }
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                      <Typography variant="h5" sx={{ mb: 1, fontWeight: 800, color: theme.palette.text.primary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        Send us a Message
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Fill out the form and we'll respond within 24 hours
                      </Typography>

                      {status.success && (
                        <Alert
                          icon={<CheckCircleIcon />}
                          severity="success"
                          sx={{ mb: 3, borderRadius: 2 }}
                          onClose={() => setStatus(prev => ({ ...prev, success: false }))}
                        >
                          Your message has been sent successfully!
                        </Alert>
                      )}

                      {status.error && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                          {status.error}
                        </Alert>
                      )}

                      <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                          fullWidth
                          label="Full Name *"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!getFieldError('name')}
                          helperText={getFieldError('name')}
                          disabled={status.loading}
                          sx={{ mb: 2.5 }}
                        />

                        <TextField
                          fullWidth
                          label="Email Address *"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!getFieldError('email')}
                          helperText={getFieldError('email')}
                          disabled={status.loading}
                          sx={{ mb: 2.5 }}
                        />

                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!getFieldError('phone')}
                          helperText={getFieldError('phone')}
                          disabled={status.loading}
                          sx={{ mb: 2.5 }}
                          placeholder="Optional"
                        />

                        <TextField
                          fullWidth
                          label="Subject *"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!getFieldError('subject')}
                          helperText={getFieldError('subject')}
                          disabled={status.loading}
                          sx={{ mb: 2.5 }}
                        />

                        <TextField
                          fullWidth
                          label="Message *"
                          name="message"
                          multiline
                          rows={5}
                          value={formData.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!getFieldError('message')}
                          helperText={getFieldError('message')}
                          disabled={status.loading}
                          sx={{ mb: 3 }}
                        />

                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={status.loading}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '0.95rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.3s ease'
                          }}
                        >
                          {status.loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            <>
                              <SendIcon sx={{ mr: 1.5 }} />
                              Send Message
                            </>
                          )}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;